/**
 * Comprehension-check aggregation for the Review Queue.
 *
 * Pure functions over plain rows: the component maps subscription rows into
 * these shapes once, so scoring and miss-rate maths stay testable without a
 * connection.
 *
 * The module exposes no cohort view onto `assignment_attempt`, so the reviewed
 * attempt's `score_pct` / `passed` are recomputed here from
 * `cohort_check_answers`. Both halves therefore have to match the module
 * exactly, and they are two different sums: `scorePct` mirrors
 * `assignments::score_pct` (integer round-half-up, never a ceiling; an
 * orientation with no checks scores 100) and is display only, while the pass
 * decision is `meetsBar` on the raw counts, mirroring `assignments::meets_bar`.
 */

/** A check as authored, in the order it presents at runtime. */
export type ReviewCheckDef = {
  checkId: string;
  stepId: string;
  beat: number;
  question: string;
  options: readonly { readonly id: string; readonly text: string }[];
  multiSelect: boolean;
  /** The `CheckGrading` tag: Any passes on any one key option, All needs the exact key set. */
  grading: 'All' | 'Any';
  /** The module's sparse ordering within a beat; several checks may share one. */
  sequenceOrder: number;
};

/** One `check_answer` row, timestamp already coerced to epoch ms. */
export type ReviewAnswer = {
  attemptNumber: number;
  checkId: string;
  tryNumber: number;
  selectedOptionIds: readonly string[];
  correct: boolean;
  expired: boolean;
  answeredAtMs: number;
};

/**
 * `picked` is the no-answer-key case: `visible_orientation_check_keys` is
 * admin-scoped in the module, so a Member-role session sees the trainee's
 * picks but cannot mark them right or wrong.
 */
export type OptionMark = 'correct' | 'wrong' | 'missed' | 'picked' | 'unpicked';

export type ReviewTry = {
  tryNumber: number;
  correct: boolean;
  expired: boolean;
  answeredAtMs: number;
  options: { id: string; text: string; mark: OptionMark }[];
};

export type ReviewCheck = {
  checkId: string;
  stepId: string;
  beat: number;
  question: string;
  multiSelect: boolean;
  grading: 'All' | 'Any';
  /** Empty when the caller cannot read the answer keys. */
  correctOptionIds: readonly string[];
  outcome: 'correct' | 'failed' | 'unanswered';
  tries: ReviewTry[];
};

export type AttemptReview = {
  attemptNumber: number;
  totalChecks: number;
  correctChecks: number;
  scorePct: number;
  /** null when the orientation has no passing bar. */
  passed: boolean | null;
  /** The bar expressed in questions, per the spec's display rule. */
  requiredChecks: number | null;
  /**
   * The module reopened the previous attempt itself. A manual send-back also
   * raises the attempt number, which is why the previous attempt's score is
   * part of the test rather than the attempt number alone.
   */
  autoReassigned: boolean;
  checks: ReviewCheck[];
};

export type CheckQuality = {
  checkId: string;
  stepId: string;
  question: string;
  /** Trainees who reached the check at all. */
  trainees: number;
  firstTryWrong: number;
  missRate: number;
  topWrongOption: { id: string; text: string; count: number } | null;
  flagged: boolean;
};

/** Over this share of trainees missing on the first try, the question is suspect. */
export const MISS_RATE_FLAG = 0.5;

export function scorePct(correct: number, total: number): number {
  if (total === 0) return 100;
  return Math.floor((correct * 200 + total) / (total * 2));
}

/**
 * Whether a run clears the bar, on the raw counts, mirroring the module's
 * `assignments::meets_bar`. Never decide this on `scorePct`: 7 of 8 rounds to
 * 88 and would pass an 88% bar the module fails, while the same line says 8 are
 * needed. No checks means nothing was asked, so no bar can fail it.
 */
export function meetsBar(correct: number, total: number, bar: number): boolean {
  return total === 0 || correct * 100 >= bar * total;
}

/** The bar expressed in questions, the spec's display rule and `meetsBar`'s threshold. */
export function requiredChecks(bar: number, total: number): number {
  return Math.ceil((bar * total) / 100);
}

export function markOption(
  optionId: string,
  selectedOptionIds: readonly string[],
  correctOptionIds: readonly string[],
): OptionMark {
  const picked = selectedOptionIds.includes(optionId);
  if (correctOptionIds.length === 0) return picked ? 'picked' : 'unpicked';
  const isCorrect = correctOptionIds.includes(optionId);
  if (picked) return isCorrect ? 'correct' : 'wrong';
  return isCorrect ? 'missed' : 'unpicked';
}

/**
 * Scores one attempt and returns the full try trail for every check in the
 * orientation, including the ones never reached.
 *
 * `attemptNumber` is the run under review, taken from the assignment's step
 * metrics rather than from the answer rows: an attempt that finished without
 * reaching a single check has no rows of its own, and the highest attempt
 * number present in `answers` would then be the *previous* run's.
 *
 * `checks` must already be in presentation order (step position, then beat,
 * then sequence order) and already filtered to the assignment's version.
 */
export function reviewAttempt(args: {
  checks: readonly ReviewCheckDef[];
  correctByCheckId: ReadonlyMap<string, readonly string[]>;
  /** Every answer for this assignment, across all attempts. */
  answers: readonly ReviewAnswer[];
  attemptNumber: number;
  passingScorePct: number | null;
  autoReassignOnFail: boolean;
}): AttemptReview {
  const { checks, correctByCheckId, answers, attemptNumber, passingScorePct } = args;

  const reviewed = checks.map((def) => {
    const correctOptionIds = correctByCheckId.get(def.checkId) ?? [];
    const tries = answers
      .filter((a) => a.attemptNumber === attemptNumber && a.checkId === def.checkId)
      .sort((a, b) => a.tryNumber - b.tryNumber)
      .map<ReviewTry>((a) => ({
        tryNumber: a.tryNumber,
        correct: a.correct,
        expired: a.expired,
        answeredAtMs: a.answeredAtMs,
        options: def.options.map((o) => ({
          id: o.id,
          text: o.text,
          mark: markOption(o.id, a.selectedOptionIds, correctOptionIds),
        })),
      }));
    const outcome: ReviewCheck['outcome'] =
      tries.length === 0 ? 'unanswered' : tries.some((t) => t.correct) ? 'correct' : 'failed';
    return {
      checkId: def.checkId,
      stepId: def.stepId,
      beat: def.beat,
      question: def.question,
      multiSelect: def.multiSelect,
      grading: def.grading,
      correctOptionIds,
      outcome,
      tries,
    };
  });

  const correctChecks = reviewed.filter((c) => c.outcome === 'correct').length;
  const pct = scorePct(correctChecks, reviewed.length);

  let autoReassigned = false;
  if (args.autoReassignOnFail && attemptNumber > 1 && passingScorePct !== null) {
    const previous = answers.filter((a) => a.attemptNumber === attemptNumber - 1);
    const previousCorrect = checks.filter((def) =>
      previous.some((a) => a.checkId === def.checkId && a.correct),
    ).length;
    autoReassigned = !meetsBar(previousCorrect, checks.length, passingScorePct);
  }

  return {
    attemptNumber,
    totalChecks: reviewed.length,
    correctChecks,
    scorePct: pct,
    passed:
      passingScorePct === null ? null : meetsBar(correctChecks, reviewed.length, passingScorePct),
    requiredChecks:
      passingScorePct === null ? null : requiredChecks(passingScorePct, reviewed.length),
    autoReassigned,
    checks: reviewed,
  };
}

/**
 * Miss rate per check across every trainee who reached it: the share whose
 * very first try (earliest attempt, try 1) was wrong, plus the wrong option
 * that dominates the picks. A distractor that outdraws the key is the signal
 * that the question, not the trainee, is the problem.
 *
 * Checks nobody has answered are dropped rather than reported at 0%.
 */
export function checkQuality(args: {
  checks: readonly ReviewCheckDef[];
  correctByCheckId: ReadonlyMap<string, readonly string[]>;
  /** Cohort answers tagged with the assignment they came from. */
  answers: readonly (ReviewAnswer & { assignmentKey: string })[];
}): CheckQuality[] {
  const { checks, correctByCheckId, answers } = args;
  const out: CheckQuality[] = [];

  for (const def of checks) {
    const rows = answers.filter((a) => a.checkId === def.checkId);
    if (rows.length === 0) continue;
    const correctOptionIds = correctByCheckId.get(def.checkId) ?? [];

    const firstTry = new Map<string, ReviewAnswer>();
    for (const row of rows) {
      const seen = firstTry.get(row.assignmentKey);
      if (
        !seen ||
        row.attemptNumber < seen.attemptNumber ||
        (row.attemptNumber === seen.attemptNumber && row.tryNumber < seen.tryNumber)
      ) {
        firstTry.set(row.assignmentKey, row);
      }
    }
    const trainees = firstTry.size;
    let firstTryWrong = 0;
    for (const row of firstTry.values()) if (!row.correct) firstTryWrong += 1;

    const wrongPicks = new Map<string, number>();
    for (const row of rows) {
      if (row.correct) continue;
      for (const id of row.selectedOptionIds) {
        if (correctOptionIds.includes(id)) continue;
        wrongPicks.set(id, (wrongPicks.get(id) ?? 0) + 1);
      }
    }
    let topWrongOption: CheckQuality['topWrongOption'] = null;
    for (const option of def.options) {
      const count = wrongPicks.get(option.id) ?? 0;
      if (count > 0 && (!topWrongOption || count > topWrongOption.count)) {
        topWrongOption = { id: option.id, text: option.text, count };
      }
    }

    const missRate = trainees === 0 ? 0 : firstTryWrong / trainees;
    out.push({
      checkId: def.checkId,
      stepId: def.stepId,
      question: def.question,
      trainees,
      firstTryWrong,
      missRate,
      topWrongOption,
      flagged: missRate > MISS_RATE_FLAG,
    });
  }

  out.sort((a, b) => b.missRate - a.missRate || b.trainees - a.trainees);
  return out;
}
